// HardwareProLayout.js - Hardware Shop Theme
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  StoreLogo, CartBtn, TabContent, StoreFooter, InlineEditableImage,
  AWS_URL, FaHeart
} from './ThemeLayoutHelpers';

// Custom InlineEditableTextWithNav component
const InlineEditableTextWithNav = ({ p, field, value, className = "", style = {}, children, onNavigate, navPage, isInModal = false }) => {
  const isEditing = p.previewEditMode && p.activeEditField === field;
  const isEditable = typeof p.onActivateField === "function";
  
  const getCurrentValue = () => {
    if (p.storeTheme && p.storeTheme[field] !== undefined && p.storeTheme[field] !== "") {
      return p.storeTheme[field];
    }
    // Also check p.themeText if available (for preview mode)
    if (p.themeText && p.themeText[field] !== undefined && p.themeText[field] !== "") {
      return p.themeText[field];
    }
    return value;
  };
  
  const [localValue, setLocalValue] = useState(getCurrentValue());

  useEffect(() => {
    if (!isEditing) {
      setLocalValue(getCurrentValue());
    }
  }, [p.storeTheme, field, value, isEditing, p.themeText]);

  const handleActivate = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const currentVal = getCurrentValue();
    setLocalValue(currentVal);
    if (typeof p.onActivateField === "function") {
      p.onActivateField(field);
    }
  };

  const handleChange = (event) => {
    const newValue = event.target.value;
    setLocalValue(newValue);
  };

  const handleSaveAndClose = () => {
    if (typeof p.onPreviewTextChange === "function") {
      p.onPreviewTextChange(field, localValue);
    }
    if (typeof p.onDeactivateField === "function") {
      p.onDeactivateField();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      handleSaveAndClose();
      if (!isInModal && onNavigate && navPage) {
        onNavigate(navPage);
      }
    }
  };

  const handleBlur = () => {
    handleSaveAndClose();
  };

  if (isEditing) {
    const inputClass = "w-full rounded-2xl border border-orange-200 bg-white/90 px-3 py-2 text-sm text-gray-900 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100 dark:border-orange-700 dark:bg-gray-800 dark:text-gray-100";
    const multiline = field.toLowerCase().includes("subtitle") || field.toLowerCase().includes("description") || field.toLowerCase().includes("overview") || field.toLowerCase().includes("content");

    return multiline ? (
      <textarea
        autoFocus
        rows={4}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    ) : (
      <input
        autoFocus
        type="text"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    );
  }

  const displayValue = p.storeTheme?.[field] || p.themeText?.[field] || value;
  
  return (
    <span
      className={`${className} ${isEditable ? 'cursor-pointer inline-block' : 'inline-block'}`}
      style={{ whiteSpace: "pre-wrap", ...style }}
      onClick={isEditable ? handleActivate : undefined}
      title={p.previewEditMode ? "Edit text" : undefined}
    >
      {displayValue}
    </span>
  );
};

export default function HardwareProLayout({ p }) {
  const T = p.storeTheme || {};
  const primary = T.primaryColor || "#ea580c";
  const secondary = T.secondaryColor || "#f59e0b";
  
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [visibleProducts, setVisibleProducts] = useState([]);
  const [activePage, setActivePage] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [animationComplete, setAnimationComplete] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");
  
  const heroRef = useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);
  
  useEffect(() => {
    setRefreshKey(prev => prev + 1);
  }, [p.storeTheme]);

  // Hardware hero images - DIRECTLY FROM CONFIG
  const hardwareHeroImageConfigs = [
    {
      field: "themePreviewImage",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/a8415eec-007c-42cb-a0e0-1084d885b373_h1.jpg",
    },
    {
      field: "hardwareHeroImage2",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/0de5efb2-1d70-4e0f-b346-7f47b0791ded_h2.jpg",
    },
    {
      field: "hardwareHeroImage3",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/47d03d18-0835-4392-89a7-cced2b7baf31_h3.jpg",
    },
    {
      field: "hardwareHeroImage4",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/abc0caab-ac1f-4a26-bf08-c2b069d56302_h4.jpg",
    },
    {
      field: "hardwareHeroImage5",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/bbf5988c-372c-43ab-9729-d40ba139c055_h5.jpg",
    },
  ];

  // Hardware Products - Added imageField
  const hardwareProducts = [
    // Power Tools
    {
      id: 1,
      nameKey: "product1_name",
      name: "Heavy-Duty Drill Machine",
      priceKey: "product1_price",
      price: 4999,
      originalPriceKey: "product1_original_price",
      originalPrice: 6999,
      imageField: "product1_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/68a3e08a-62d5-4c1f-b1f3-b247d9f85625_h6.jpg",
      categoryKey: "product1_category",
      category: "Power Tools",
      subCategory: "Drills",
      descriptionKey: "product1_description",
      description: "1500W heavy-duty drill with 13mm chuck, ideal for professional use.",
      badge: "Bestseller",
      badgeColor: "#ea580c",
      brand: "BoschPro",
      voltage: "220V",
      warranty: "2 Years",
      power: "1500W"
    },
    {
      id: 2,
      nameKey: "product2_name",
      name: "Angle Grinder 4\"",
      priceKey: "product2_price",
      price: 2999,
      originalPriceKey: "product2_original_price",
      originalPrice: 3999,
      imageField: "product2_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/81b5c5db-a044-409a-a99e-154d0be8339e_h7.jpg",
      categoryKey: "product2_category",
      category: "Power Tools",
      subCategory: "Grinders",
      descriptionKey: "product2_description",
      description: "4-inch angle grinder with 850W motor for cutting and grinding.",
      badge: "Popular",
      badgeColor: "#f59e0b",
      brand: "Makita",
      voltage: "220V",
      warranty: "1 Year",
      power: "850W"
    },
    {
      id: 3,
      nameKey: "product3_name",
      name: "Jigsaw 500W",
      priceKey: "product3_price",
      price: 3499,
      originalPriceKey: "product3_original_price",
      originalPrice: 4999,
      imageField: "product3_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/419cc457-a456-4a09-9c4d-92873b03a3c0_h8.jpg",
      categoryKey: "product3_category",
      category: "Power Tools",
      subCategory: "Saws",
      descriptionKey: "product3_description",
      description: "500W jigsaw with variable speed for precise curved cuts.",
      badge: "Precision",
      badgeColor: "#8b5cf6",
      brand: "Black+Decker",
      voltage: "220V",
      warranty: "2 Years",
      power: "500W"
    },
    
    // Hand Tools
    {
      id: 4,
      nameKey: "product4_name",
      name: "Premium Tool Kit (32 pcs)",
      priceKey: "product4_price",
      price: 2999,
      originalPriceKey: "product4_original_price",
      originalPrice: 4499,
      imageField: "product4_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/18cb9569-c743-49fd-a87d-b1e73f5cac1d_h9.webp",
      categoryKey: "product4_category",
      category: "Hand Tools",
      subCategory: "Tool Kits",
      descriptionKey: "product4_description",
      description: "Complete 32-piece tool kit with ratchet, sockets, and screwdrivers.",
      badge: "Value Pack",
      badgeColor: "#16a34a",
      brand: "Stanley",
      pieces: "32",
      warranty: "Lifetime",
      material: "Chrome Vanadium"
    },
    {
      id: 5,
      nameKey: "product5_name",
      name: "Adjustable Wrench 12\"",
      priceKey: "product5_price",
      price: 599,
      originalPriceKey: "product5_original_price",
      originalPrice: 899,
      imageField: "product5_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/d794f4e8-c1d9-42b2-a3dd-1c7bc9844c21_Adjustable_Wrench_12.jpg",
      categoryKey: "product5_category",
      category: "Hand Tools",
      subCategory: "Wrenches",
      descriptionKey: "product5_description",
      description: "12-inch heavy-duty adjustable wrench with chrome finish.",
      badge: "Essential",
      badgeColor: "#f59e0b",
      brand: "Craftsman",
      size: "12\"",
      warranty: "Lifetime",
      material: "Forged Steel"
    },
    {
      id: 6,
      nameKey: "product6_name",
      name: "Hammer Set (3 pcs)",
      priceKey: "product6_price",
      price: 899,
      originalPriceKey: "product6_original_price",
      originalPrice: 1299,
      imageField: "product6_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/844ea745-370e-4b49-a6d5-9f4d7f3b6b44_Hammer_Set_(3_pcs).jpg",
      categoryKey: "product6_category",
      category: "Hand Tools",
      subCategory: "Hammers",
      descriptionKey: "product6_description",
      description: "Set of 3 hammers - claw, sledge, and rubber mallet.",
      badge: "Versatile",
      badgeColor: "#8b5cf6",
      brand: "Estwing",
      pieces: "3",
      warranty: "Lifetime",
      material: "Steel & Rubber"
    },
    
    // Building Materials
    {
      id: 7,
      nameKey: "product7_name",
      name: "Cement (50kg)",
      priceKey: "product7_price",
      price: 399,
      originalPriceKey: "product7_original_price",
      originalPrice: 549,
      imageField: "product7_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/f5aa618a-4d9d-4dc0-b2df-a790287f837e_Cement_(50kg).jpg",
      categoryKey: "product7_category",
      category: "Building Materials",
      subCategory: "Cement",
      descriptionKey: "product7_description",
      description: "Premium quality OPC 53 grade cement for construction.",
      badge: "Construction",
      badgeColor: "#ea580c",
      brand: "UltraTech",
      weight: "50kg",
      grade: "OPC 53",
      type: "Ordinary Portland Cement"
    },
    {
      id: 8,
      nameKey: "product8_name",
      name: "PVC Pipe (4m)",
      priceKey: "product8_price",
      price: 299,
      originalPriceKey: "product8_original_price",
      originalPrice: 449,
      imageField: "product8_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/70dc3897-70c8-4aaf-accb-1ef5d7aad1b0_PVC_Pipe_(4m).webp",
      categoryKey: "product8_category",
      category: "Building Materials",
      subCategory: "Plumbing",
      descriptionKey: "product8_description",
      description: "4-meter PVC pipe for plumbing and drainage applications.",
      badge: "Plumbing",
      badgeColor: "#059669",
      brand: "Finolex",
      length: "4m",
      diameter: "2\"",
      type: "PVC"
    },
    {
      id: 9,
      nameKey: "product9_name",
      name: "Tiles (Box of 12)",
      priceKey: "product9_price",
      price: 899,
      originalPriceKey: "product9_original_price",
      originalPrice: 1299,
      imageField: "product9_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/50fb326b-c2ae-4523-86cc-76747ce104c3_Tiles_(Box_of_12).avif",
      categoryKey: "product9_category",
      category: "Building Materials",
      subCategory: "Tiles",
      descriptionKey: "product9_description",
      description: "Premium vitrified tiles for flooring and walls.",
      badge: "Flooring",
      badgeColor: "#8b5cf6",
      brand: "Kajaria",
      quantity: "12 pcs",
      size: "2x2 ft",
      type: "Vitrified"
    },
    
    // Paints
    {
      id: 10,
      nameKey: "product10_name",
      name: "Wall Paint (4L)",
      priceKey: "product10_price",
      price: 1499,
      originalPriceKey: "product10_original_price",
      originalPrice: 1999,
      imageField: "product10_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/279c4331-4ea3-4e5c-9327-582163102bab_Wall_Paint_(4L).jpg",
      categoryKey: "product10_category",
      category: "Paints",
      subCategory: "Wall Paints",
      descriptionKey: "product10_description",
      description: "Premium emulsion paint for interior walls with smooth finish.",
      badge: "Premium",
      badgeColor: "#ea580c",
      brand: "Asian Paints",
      volume: "4L",
      coverage: "16 sq.m/L",
      type: "Emulsion"
    },
    {
      id: 11,
      nameKey: "product11_name",
      name: "Primer (1L)",
      priceKey: "product11_price",
      price: 499,
      originalPriceKey: "product11_original_price",
      originalPrice: 699,
      imageField: "product11_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/d26e001a-8133-4068-9b2f-ea47a86e6d8d_Primer_(1L).jpg",
      categoryKey: "product11_category",
      category: "Paints",
      subCategory: "Primers",
      descriptionKey: "product11_description",
      description: "Wall primer for better paint adhesion and durability.",
      badge: "Essential",
      badgeColor: "#f59e0b",
      brand: "Berger",
      volume: "1L",
      coverage: "12 sq.m/L",
      type: "Primer"
    },
    
    // Electrical
    {
      id: 12,
      nameKey: "product12_name",
      name: "Electrical Wire (90m)",
      priceKey: "product12_price",
      price: 1299,
      originalPriceKey: "product12_original_price",
      originalPrice: 1799,
      imageField: "product12_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/32e388fa-daae-4570-a753-5d283557412f_Electrical_Wire_(90m).jpg",
      categoryKey: "product12_category",
      category: "Electrical",
      subCategory: "Wires & Cables",
      descriptionKey: "product12_description",
      description: "High-quality copper wire for residential electrical wiring.",
      badge: "Electrical",
      badgeColor: "#dc2626",
      brand: "Havells",
      length: "90m",
      gauge: "2.5 sqmm",
      type: "Copper"
    },
    {
      id: 13,
      nameKey: "product13_name",
      name: "Switch Board 6-way",
      priceKey: "product13_price",
      price: 799,
      originalPriceKey: "product13_original_price",
      originalPrice: 1099,
      imageField: "product13_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/d931d64f-61c3-4244-8d07-a99d192aeaf4_Switch_Board_6-way.webp",
      categoryKey: "product13_category",
      category: "Electrical",
      subCategory: "Switches",
      descriptionKey: "product13_description",
      description: "6-way switch board with high-quality switches and sockets.",
      badge: "Safety",
      badgeColor: "#16a34a",
      brand: "Anchor",
      ways: "6",
      material: "Polycarbonate",
      warranty: "1 Year"
    }
  ];

  // Categories
  const productCategories = [
    { key: "all", icon: "🔧", label: "All Products", color: "#ea580c" },
    { key: "powertools", icon: "⚡", label: "Power Tools", color: "#dc2626" },
    { key: "handtools", icon: "🔨", label: "Hand Tools", color: "#f59e0b" },
    { key: "building", icon: "🏗️", label: "Building Materials", color: "#ea580c" },
    { key: "paints", icon: "🎨", label: "Paints", color: "#8b5cf6" },
    { key: "electrical", icon: "💡", label: "Electrical", color: "#059669" },
  ];

  // DIY Guides - Added imageField
  const diyGuides = [
    {
      id: 1,
      titleKey: "guide1_title",
      title: "How to Use a Drill",
      descKey: "guide1_desc",
      desc: "Master the basics of drilling like a pro.",
      imageField: "guide1_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/f5758cc1-a760-4dd7-98c6-1a18ed3fe08e_How_to_Use_a_Drill.webp",
      steps: [
        "Select the right drill bit for your material",
        "Mark your drilling spot with a pencil",
        "Set the drill to the correct speed",
        "Apply steady pressure while drilling",
        "Clean the hole and finish the job"
      ],
      difficulty: "Beginner"
    },
    {
      id: 2,
      titleKey: "guide2_title",
      title: "Painting Techniques",
      descKey: "guide2_desc",
      desc: "Professional painting tips for perfect walls.",
      imageField: "guide2_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/958b4935-7629-49ae-8347-4ca8c860fc0a_Painting_Techniques.jpg",
      steps: [
        "Prepare the surface by cleaning and sanding",
        "Apply primer for better adhesion",
        "Use quality brushes and rollers",
        "Paint in sections with consistent strokes",
        "Apply second coat after drying"
      ],
      difficulty: "Intermediate"
    },
    {
      id: 3,
      titleKey: "guide3_title",
      title: "Basic Plumbing Fixes",
      descKey: "guide3_desc",
      desc: "Common plumbing issues you can fix yourself.",
      imageField: "guide3_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/8930a010-fa99-4786-bc44-341ec1c82765_Basic_Plumbing_Fixes.jpg",
      steps: [
        "Identify the type of plumbing issue",
        "Gather the right tools and parts",
        "Turn off water supply before working",
        "Apply Teflon tape on threads",
        "Test for leaks after repair"
      ],
      difficulty: "Intermediate"
    }
  ];

  // Blog Posts - Added imageField
  const editorialPosts = [
    {
      id: 1,
      titleKey: "post1_title",
      title: "Home Improvement Guide",
      descKey: "post1_desc",
      desc: "Essential tips for your next renovation project.",
      fullContentKey: "post1_content",
      fullContent: "Renovating your home can be overwhelming. From planning to execution, learn the essential steps for a successful home improvement project. Discover what tools you need, how to budget, and when to call professionals.",
      imageField: "post1_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/0ab3f082-0c15-42a9-bc04-cf49c42b70e1_Home_Improvement_Guide.webp",
      dateKey: "post1_date",
      date: "March 15, 2024",
      authorKey: "post1_author",
      author: "John Builder",
      category: "Renovation"
    },
    {
      id: 2,
      titleKey: "post2_title",
      title: "Power Tool Safety",
      descKey: "post2_desc",
      desc: "Essential safety tips for using power tools.",
      fullContentKey: "post2_content",
      fullContent: "Power tools are incredibly useful but can be dangerous if not used properly. Learn the essential safety guidelines, from wearing proper PPE to understanding tool limitations and maintenance.",
      imageField: "post2_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/ebd48b83-a955-4d25-89ef-a7d16133e4bb_Power_Tool_Safety.jpg",
      dateKey: "post2_date",
      date: "March 10, 2024",
      authorKey: "post2_author",
      author: "Safety Sam",
      category: "Safety"
    },
    {
      id: 3,
      titleKey: "post3_title",
      title: "Choosing the Right Paint",
      descKey: "post3_desc",
      desc: "How to select the perfect paint for your project.",
      fullContentKey: "post3_content",
      fullContent: "With so many paint options available, choosing the right one can be confusing. Learn about different paint types, finishes, and how to select the perfect color for your space.",
      imageField: "post3_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/d3b0e431-8c6c-4483-ae83-ccce2c8927a0_Choosing_the_Right_Paint.jpg",
      dateKey: "post3_date",
      date: "March 5, 2024",
      authorKey: "post3_author",
      author: "Color Expert",
      category: "Painting"
    }
  ];

  // Navigation items
  const navItems = [
    { key: "nav_home", label: "HOME", page: "home" },
    { key: "nav_powertools", label: "POWER TOOLS", page: "powertools" },
    { key: "nav_handtools", label: "HAND TOOLS", page: "handtools" },
    { key: "nav_building", label: "BUILDING", page: "building" },
    { key: "nav_paints", label: "PAINTS", page: "paints" },
    { key: "nav_electrical", label: "ELECTRICAL", page: "electrical" },
    { key: "nav_journal", label: "JOURNAL", page: "journal" },
    { key: "nav_about", label: "ABOUT", page: "about" },
  ];

  // Top bar messages
  const topBarMessages = [
    "🔧 HARDWARE & CONSTRUCTION",
    "✦ QUALITY TOOLS & MATERIALS",
    "⚡ TRUSTED BRANDS",
    "✦ FREE DELIVERY ON BULK ORDERS"
  ];

  // Floating particles
  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    top: Math.random() * 100,
    size: 3 + Math.random() * 5,
    delay: Math.random() * 5,
    duration: 5 + Math.random() * 5,
    opacity: 0.05 + Math.random() * 0.15,
  }));

  // Auto-rotate hero images - USING CONFIG DIRECTLY
  useEffect(() => {
    if (p.previewEditMode) return;
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % hardwareHeroImageConfigs.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [p.previewEditMode]);

  // Animate products
  useEffect(() => {
    const products = p.filteredProducts || [];
    if (products.length === 0) return;
    
    setTimeout(() => setAnimationComplete(true), 300);
    
    products.forEach((_, index) => {
      setTimeout(() => {
        setVisibleProducts(prev => [...prev, index]);
      }, index * 80 + 500);
    });
  }, [p.filteredProducts]);

  // Mouse move for parallax
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 15,
        y: (e.clientY / window.innerHeight - 0.5) * 10,
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // Scroll handler
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navigateTo = useCallback((page, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setActivePage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [p.previewEditMode, p.activeEditField]);

  const handleNavigateAfterEdit = useCallback((page) => {
    setActivePage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const openBlogModal = useCallback((post, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setSelectedPost(post);
  }, [p.previewEditMode, p.activeEditField]);

  const closeBlogModal = useCallback(() => {
    setSelectedPost(null);
  }, []);

  const openGuideModal = useCallback((guide, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setSelectedGuide(guide);
  }, [p.previewEditMode, p.activeEditField]);

  const closeGuideModal = useCallback(() => {
    setSelectedGuide(null);
  }, []);

  const handleBackdropClick = useCallback((event) => {
    if (event.target === event.currentTarget) {
      if (p.previewEditMode && p.activeEditField) {
        return;
      }
      setSelectedProduct(null);
      setSelectedPost(null);
      setSelectedGuide(null);
    }
  }, [p.previewEditMode, p.activeEditField]);

  const handleEditHeroImage = useCallback((event, field = null) => {
    event.preventDefault();
    event.stopPropagation();
    if (!p.previewEditMode) {
      return;
    }
    const targetField = field || hardwareHeroImageConfigs[activeSlide]?.field || "themePreviewImage";
    const targetIndex = hardwareHeroImageConfigs.findIndex((image) => image.field === targetField);
    if (targetIndex >= 0) {
      setActiveSlide(targetIndex);
    }
    if (typeof p.onImageFieldClick === "function") {
      p.onImageFieldClick(targetField, event);
      return;
    }
    if (typeof p.onActivateField === "function") {
      p.onActivateField(targetField);
    }
  }, [activeSlide, hardwareHeroImageConfigs, p]);

  const copy = {
    eyebrow: p.storeTheme?.heroEyebrow || "HARDWARE PRO",
    title: p.storeTheme?.heroTitle || "Build with Confidence",
    subtitle: p.storeTheme?.heroSubtitle || "Quality tools and materials for every project",
    primaryCta: p.storeTheme?.primaryCtaText || "Shop Now",
    secondaryCta: p.storeTheme?.secondaryCtaText || "View Guides",
  };

  // Get editable products with imageField
  const editableHardwareProducts = hardwareProducts.map((product) => ({
    ...product,
    image: p.storeTheme?.[product.imageField] || product.image,
  }));

  // Get editable guides with imageField
  const editableDiyGuides = diyGuides.map((guide) => ({
    ...guide,
    image: p.storeTheme?.[guide.imageField] || guide.image,
  }));

  // Get editable posts with imageField
  const editableEditorialPosts = editorialPosts.map((post) => ({
    ...post,
    image: p.storeTheme?.[post.imageField] || post.image,
  }));

  // Get filtered items based on category
  const getFilteredItems = (category) => {
    if (category === "all") return editableHardwareProducts;
    const map = {
      "powertools": "Power Tools",
      "handtools": "Hand Tools",
      "building": "Building Materials",
      "paints": "Paints",
      "electrical": "Electrical",
    };
    return editableHardwareProducts.filter(item => item.category === map[category]);
  };

  const filteredItems = getFilteredItems(activeCategory);

  // Get card entrance animation
  const getCardEntrance = (index) => {
    const directions = [
      { x: -40, y: -15, rotate: -3 },
      { x: 40, y: -15, rotate: 3 },
      { x: -40, y: 15, rotate: -2 },
      { x: 40, y: 15, rotate: 2 },
    ];
    return directions[index % directions.length];
  };

  return (
    <div key={refreshKey} className="w-full min-h-screen bg-orange-50 overflow-x-hidden" style={{ fontFamily: T.fontFamily || "'Inter', sans-serif" }}>
      
      {/* Floating Particles */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="absolute rounded-full animate-float-particle"
            style={{
              left: `${particle.left}%`,
              top: `${particle.top}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              background: `radial-gradient(circle, ${primary}${Math.floor(20 + Math.random() * 40).toString(16)}, transparent)`,
              animationDelay: `${particle.delay}s`,
              animationDuration: `${particle.duration}s`,
              opacity: particle.opacity,
            }}
          />
        ))}
      </div>
      
      {/* Top Bar */}
      <div className="relative bg-[#2d1b0e] text-orange-200 py-2 overflow-hidden">
        <div className="whitespace-nowrap animate-marquee">
          {topBarMessages.map((msg, idx) => (
            <span key={idx} className="inline-block mx-8 text-xs tracking-wider text-white/80">
              <InlineEditableTextWithNav 
                p={p} 
                field={`top_bar_message_${idx + 1}`} 
                value={msg}
                onNavigate={handleNavigateAfterEdit}
                navPage="home"
              />
            </span>
          ))}
        </div>
      </div>
      
      {/* Navigation */}
      <nav className={`sticky top-0 z-50 transition-all duration-500 ${isScrolled ? "bg-[#2d1b0e]/95 backdrop-blur-xl shadow-lg py-2" : "bg-[#2d1b0e]/90 backdrop-blur-sm py-4"}`}>
        <div className="w-full px-4 md:px-8 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={(e) => navigateTo("home", e)}>
            <StoreLogo storeInfo={p.storeInfo} border={`border-2 ${isScrolled ? "border-orange-400" : "border-orange-300"}`} />
            <span className="font-bold text-xl tracking-tight text-white">
              <InlineEditableTextWithNav 
                p={p} 
                field="businessName" 
                value={p.storeInfo?.businessName || "Hardware Pro"} 
                onNavigate={handleNavigateAfterEdit}
                navPage="home"
              />
            </span>
          </div>
          
          <button 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!(p.previewEditMode && p.activeEditField)) {
                setMobileMenuOpen(!mobileMenuOpen);
              }
            }} 
            className="md:hidden p-2"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          
          <div className="hidden md:flex items-center gap-6">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={(e) => navigateTo(item.page, e)}
                className={`text-sm font-medium transition-all duration-300 relative group ${
                  activePage === item.page 
                    ? "text-orange-400" 
                    : "text-white/80 hover:text-white"
                }`}
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field={`nav_${item.page}`} 
                  value={item.label}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={item.page}
                  className="text-inherit"
                />
                <span className={`absolute -bottom-1 left-0 h-0.5 bg-orange-400 transition-all duration-300 ${activePage === item.page ? "w-full" : "w-0 group-hover:w-full"}`} />
              </button>
            ))}
          </div>
          
          <div className="flex items-center gap-3">
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  p.handleGoToWishlist?.();
                }
              }} 
              className="relative group"
            >
              <div className="p-2 rounded-full transition-all duration-300 hover:bg-orange-800/50">
                <FaHeart className="text-lg text-white/80 hover:text-white transition-colors" />
              </div>
              {p.wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-orange-500 text-white text-[10px] flex items-center justify-center font-bold animate-pulse">
                  {p.wishlistCount}
                </span>
              )}
            </button>
            <CartBtn p={p} dark={true} />
            {p.userMenuElement}
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-[#2d1b0e] shadow-lg py-4 px-4 flex flex-col gap-2 z-50 border-t border-orange-800/30">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={(e) => navigateTo(item.page, e)}
                className={`text-sm py-2 px-3 rounded-lg text-left ${
                  activePage === item.page 
                    ? "bg-orange-800/30 text-orange-400" 
                    : "text-white/80 hover:text-white"
                }`}
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field={`nav_${item.page}`} 
                  value={item.label}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={item.page}
                  className="text-inherit"
                />
              </button>
            ))}
          </div>
        )}
      </nav>
      
      {/* ==================== HOME PAGE ==================== */}
      {activePage === "home" && (
        <>
          {/* Hero Section - FIXED: DIRECTLY USING CONFIG IMAGES - REMOVED HERO BUTTONS */}
          <section className="relative w-full h-screen min-h-[700px] overflow-hidden -mt-[60px]">
            <div className="absolute inset-0 w-full h-full">
              {hardwareHeroImageConfigs.map((img, idx) => (
                <div
                  key={img.field}
                  className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ease-in-out ${
                    idx === activeSlide ? "opacity-100 z-10" : "opacity-0 z-0"
                  }`}
                >
                  <InlineEditableImage p={p} field={img.field} className="h-full w-full">
                    <img 
                      src={p.storeTheme?.[img.field] || img.src} 
                      alt={`Hardware ${idx + 1}`} 
                      className="w-full h-full object-cover object-center" 
                      style={{ 
                        transform: `translate(${mousePosition.x * 0.3}px, ${mousePosition.y * 0.3}px) scale(1.05)`, 
                        transition: "transform 0.3s ease-out" 
                      }} 
                    />
                  </InlineEditableImage>
                </div>
              ))}
              <div className="absolute inset-0 bg-gradient-to-r from-[#2d1b0e]/90 via-[#2d1b0e]/60 to-transparent z-20 pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-t from-orange-50 via-transparent to-transparent z-20 pointer-events-none" />
            </div>
            
            <div className="relative z-30 h-full w-full px-4 md:px-8 flex items-center">
              <div className="max-w-3xl">
                <div className="inline-block mb-6 animate-bounce-in">
                  <div className="flex items-center gap-3 px-5 py-2.5 bg-[#2d1b0e]/40 backdrop-blur-md rounded-full border border-orange-400/30">
                    <span className="w-2 h-2 rounded-full bg-orange-400 animate-ping" />
                    <span className="text-orange-200 text-xs tracking-[0.2em] uppercase">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="heroEyebrow" 
                        value={copy.eyebrow}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="home"
                      />
                    </span>
                    <span className="w-2 h-2 rounded-full bg-orange-400 animate-pulse" />
                  </div>
                </div>
                <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-[1.05] tracking-tight">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="heroTitle" 
                    value={copy.title}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  >
                    {copy.title.split(" ").map((word, i) => (
                      <span key={i} className="inline-block animate-word-pop" style={{ animationDelay: `${i * 0.15}s` }}>{word}{" "}</span>
                    ))}
                  </InlineEditableTextWithNav>
                </h1>
                <p className="mt-6 text-orange-200/90 text-lg max-w-xl animate-slide-right">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="heroSubtitle" 
                    value={copy.subtitle}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  />
                </p>
                <div className="mt-10 flex flex-wrap gap-4">
                  <button 
                    onClick={(e) => navigateTo("powertools", e)} 
                    className="group px-8 py-4 rounded-full bg-orange-600 text-white font-semibold tracking-wide transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-orange-600/30 flex items-center gap-2 animate-slide-up"
                  >
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="primaryCtaText" 
                      value={copy.primaryCta}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="powertools"
                    />
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </button>
                  <button 
                    onClick={(e) => navigateTo("handtools", e)} 
                    className="px-8 py-4 rounded-full border-2 border-orange-400/50 text-orange-200 font-semibold tracking-wide transition-all duration-300 hover:bg-orange-600/20 hover:border-orange-300 backdrop-blur-sm animate-slide-up"
                  >
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="secondaryCtaText" 
                      value={copy.secondaryCta}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="handtools"
                    />
                  </button>
                </div>
              </div>
            </div>
            
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce-slow z-30">
              <div className="flex flex-col items-center gap-1">
                <span className="text-orange-200/60 text-[10px] tracking-wider">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="scroll_text" 
                    value="SCROLL TO EXPLORE"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  />
                </span>
                <div className="w-5 h-8 border-2 border-orange-400/30 rounded-full flex justify-center">
                  <div className="w-1 h-2 bg-orange-400/60 rounded-full mt-1 animate-scroll-dot" />
                </div>
              </div>
            </div>
          </section>
          
          {/* Featured Badge - Positioned lower with more margin */}
          <div className="relative z-10 -mt-2 text-center">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-white rounded-full shadow-lg border border-orange-200 mt-2">
              <span className="text-orange-600 text-sm">🔧</span>
              <span className="text-[#2d1b0e] text-xs font-semibold tracking-wider">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="collection_badge" 
                  value="TRUSTED BY PROFESSIONALS"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </span>
              <span className="text-orange-600 text-sm">🔧</span>
            </div>
          </div>
          
          {/* Category Grid */}
          <section className="w-full px-4 md:px-8 py-12">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {productCategories.map((cat, idx) => (
                <div 
                  key={cat.key} 
                  className="group cursor-pointer animate-card-fly" 
                  style={{ animationDelay: `${idx * 0.08}s` }}
                  onClick={(e) => {
                    if (cat.key === "all") {
                      navigateTo("powertools", e);
                    } else {
                      setActiveCategory(cat.key);
                      navigateTo(cat.key === "powertools" ? "powertools" : cat.key === "handtools" ? "handtools" : cat.key === "building" ? "building" : cat.key === "paints" ? "paints" : "electrical", e);
                    }
                  }}
                >
                  <div className="relative overflow-hidden rounded-2xl p-4 text-center transition-all duration-500 hover:-translate-y-2 hover:shadow-xl bg-white border border-orange-200 hover:border-orange-400">
                    <div className="text-3xl mb-2 transition-all duration-300 group-hover:scale-110">
                      {cat.icon}
                    </div>
                    <h3 className="font-semibold text-[#2d1b0e] text-xs">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={`cat_${cat.key}`} 
                        value={cat.label}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="powertools"
                      />
                    </h3>
                    <div className="mt-1 h-0.5 w-0 group-hover:w-full transition-all duration-500 mx-auto" style={{ backgroundColor: cat.color }} />
                  </div>
                </div>
              ))}
            </div>
          </section>
          
          {/* Featured Products - FIXED with InlineEditableImage - REMOVED ADD TO CART */}
          <section className="w-full px-4 md:px-8 py-8 bg-white">
            <div className="text-center mb-10">
              <span className="text-orange-700 text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="featured_badge" 
                  value="Our Products"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#2d1b0e] mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="featured_title" 
                  value="Top Tools & Materials"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-orange-600 to-orange-400 mx-auto mt-4 rounded-full" />
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {editableHardwareProducts.slice(0, 4).map((product, idx) => {
                const entrance = getCardEntrance(idx);
                const isVisible = animationComplete;
                return (
                  <div 
                    key={product.id} 
                    className={`group cursor-pointer transition-all duration-700 ${isVisible ? "opacity-100 translate-x-0 translate-y-0 rotate-0" : `opacity-0 translate-x-[${entrance.x}px] translate-y-[${entrance.y}px] rotate-[${entrance.rotate}deg]`}`}
                    onMouseEnter={() => setHoveredCard(idx)}
                    onMouseLeave={() => setHoveredCard(null)}
                    onClick={() => setSelectedProduct(product)}
                  >
                    <div className="relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-orange-100 hover:border-orange-300">
                      <div className="relative h-48 overflow-hidden bg-orange-50">
                        <InlineEditableImage p={p} field={product.imageField} className="block h-full w-full overflow-hidden">
                          <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                        </InlineEditableImage>
                        {product.badge && (
                          <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: product.badgeColor }}>
                            {product.badge}
                          </div>
                        )}
                        <div className="absolute top-2 right-2 bg-[#2d1b0e]/80 text-orange-100 text-[10px] font-bold px-2 py-0.5 rounded-full">
                          {product.brand}
                        </div>
                      </div>
                      <div className="p-3">
                        <div className="flex items-center gap-1 mb-1">
                          <span className="text-xs text-gray-500">{product.category}</span>
                          <span className="text-xs text-gray-300">•</span>
                          <span className="text-xs text-gray-500">{product.subCategory}</span>
                        </div>
                        <h3 className="font-semibold text-gray-800 text-sm line-clamp-1">
                          <InlineEditableTextWithNav 
                            p={p} 
                            field={product.nameKey} 
                            value={product.name}
                            onNavigate={handleNavigateAfterEdit}
                            navPage="home"
                          />
                        </h3>
                        <div className="mt-2 flex items-center justify-between">
                          <div>
                            <span className="text-lg font-bold text-orange-700">₹<InlineEditableTextWithNav 
                              p={p} 
                              field={product.priceKey} 
                              value={product.price.toString()}
                              onNavigate={handleNavigateAfterEdit}
                              navPage="home"
                            /></span>
                            {product.originalPrice > product.price && (
                              <span className="text-xs text-gray-400 line-through ml-1">₹{product.originalPrice}</span>
                            )}
                          </div>
                          {product.warranty && (
                            <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                              {product.warranty}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="text-center mt-10">
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!(p.previewEditMode && p.activeEditField)) {
                    setActiveCategory("all");
                    setActivePage("powertools");
                    setMobileMenuOpen(false);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }
                }} 
                className="px-8 py-3 rounded-full bg-orange-700 text-white font-semibold hover:bg-orange-800 transition-all duration-300 hover:scale-105"
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field="view_all_button" 
                  value="View All Products"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="powertools"
                />
              </button>
            </div>
          </section>
          
          {/* DIY Guides Preview - FIXED with InlineEditableImage */}
          <section className="w-full px-4 md:px-8 py-8">
            <div className="text-center mb-8">
              <span className="text-orange-700 text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="guides_badge" 
                  value="DIY Guides"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="guides"
                />
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {editableDiyGuides.map((guide) => (
                <div 
                  key={guide.id} 
                  className="group cursor-pointer rounded-2xl overflow-hidden bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-orange-200 hover:border-orange-400"
                  onClick={() => openGuideModal(guide)}
                >
                  <div className="h-40 overflow-hidden">
                    <InlineEditableImage p={p} field={guide.imageField} className="block h-full w-full overflow-hidden">
                      <img src={guide.image} alt={guide.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    </InlineEditableImage>
                  </div>
                  <div className="p-4">
                    <h4 className="font-semibold text-[#2d1b0e] text-sm">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={guide.titleKey} 
                        value={guide.title}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="guides"
                      />
                    </h4>
                    <p className="text-xs text-gray-600 mt-1">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={guide.descKey} 
                        value={guide.desc}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="guides"
                      />
                    </p>
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                        {guide.difficulty}
                      </span>
                      <span className="text-xs text-orange-600 font-semibold group-hover:gap-2 transition-all flex items-center gap-1">
                        Read More <span>→</span>
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
      
      {/* ==================== POWER TOOLS PAGE ==================== */}
      {activePage === "powertools" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-red-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="powertools_badge" 
                value="Power Tools"
                onNavigate={handleNavigateAfterEdit}
                navPage="powertools"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2d1b0e] mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="powertools_title" 
                value="Professional Power Tools"
                onNavigate={handleNavigateAfterEdit}
                navPage="powertools"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="powertools_subtitle" 
                value="Heavy-duty tools for professional and DIY projects"
                onNavigate={handleNavigateAfterEdit}
                navPage="powertools"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-red-600 to-red-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {editableHardwareProducts.filter(d => d.category === "Power Tools").map((product) => (
              <div 
                key={product.id} 
                className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-red-200 hover:border-red-400"
                onClick={() => setSelectedProduct(product)}
              >
                <div className="relative h-48 overflow-hidden bg-red-50">
                  <InlineEditableImage p={p} field={product.imageField} className="block h-full w-full overflow-hidden">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </InlineEditableImage>
                  {product.badge && (
                    <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: product.badgeColor }}>
                      {product.badge}
                    </div>
                  )}
                  <div className="absolute top-2 right-2 bg-red-700/80 text-red-100 text-[10px] font-bold px-2 py-0.5 rounded-full">
                    {product.power}
                  </div>
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-1 mb-1">
                    <span className="text-xs text-gray-500">{product.brand}</span>
                    <span className="text-xs text-gray-300">•</span>
                    <span className="text-xs text-gray-500">{product.subCategory}</span>
                  </div>
                  <h3 className="font-semibold text-gray-800 text-sm">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={product.nameKey} 
                      value={product.name}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="powertools"
                    />
                  </h3>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-lg font-bold text-red-700">₹<InlineEditableTextWithNav 
                      p={p} 
                      field={product.priceKey} 
                      value={product.price.toString()}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="powertools"
                    /></span>
                    {product.originalPrice > product.price && (
                      <span className="text-xs text-gray-400 line-through">₹{product.originalPrice}</span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    {product.voltage} • {product.warranty}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== HAND TOOLS PAGE ==================== */}
      {activePage === "handtools" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-yellow-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="handtools_badge" 
                value="Hand Tools"
                onNavigate={handleNavigateAfterEdit}
                navPage="handtools"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2d1b0e] mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="handtools_title" 
                value="Quality Hand Tools"
                onNavigate={handleNavigateAfterEdit}
                navPage="handtools"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="handtools_subtitle" 
                value="Durable tools for everyday use"
                onNavigate={handleNavigateAfterEdit}
                navPage="handtools"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-yellow-600 to-yellow-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {editableHardwareProducts.filter(d => d.category === "Hand Tools").map((product) => (
              <div 
                key={product.id} 
                className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-yellow-200 hover:border-yellow-400"
                onClick={() => setSelectedProduct(product)}
              >
                <div className="relative h-48 overflow-hidden bg-yellow-50">
                  <InlineEditableImage p={p} field={product.imageField} className="block h-full w-full overflow-hidden">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </InlineEditableImage>
                  {product.badge && (
                    <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: product.badgeColor }}>
                      {product.badge}
                    </div>
                  )}
                  <div className="absolute top-2 right-2 bg-yellow-700/80 text-yellow-100 text-[10px] font-bold px-2 py-0.5 rounded-full">
                    {product.material}
                  </div>
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-1 mb-1">
                    <span className="text-xs text-gray-500">{product.brand}</span>
                    <span className="text-xs text-gray-300">•</span>
                    <span className="text-xs text-gray-500">{product.subCategory}</span>
                  </div>
                  <h3 className="font-semibold text-gray-800 text-sm">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={product.nameKey} 
                      value={product.name}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="handtools"
                    />
                  </h3>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-lg font-bold text-yellow-700">₹<InlineEditableTextWithNav 
                      p={p} 
                      field={product.priceKey} 
                      value={product.price.toString()}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="handtools"
                    /></span>
                    {product.originalPrice > product.price && (
                      <span className="text-xs text-gray-400 line-through">₹{product.originalPrice}</span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    {product.warranty}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== BUILDING MATERIALS PAGE ==================== */}
      {activePage === "building" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-orange-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="building_badge" 
                value="Building Materials"
                onNavigate={handleNavigateAfterEdit}
                navPage="building"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2d1b0e] mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="building_title" 
                value="Construction Materials"
                onNavigate={handleNavigateAfterEdit}
                navPage="building"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="building_subtitle" 
                value="Quality materials for your construction needs"
                onNavigate={handleNavigateAfterEdit}
                navPage="building"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-orange-600 to-orange-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {editableHardwareProducts.filter(d => d.category === "Building Materials").map((product) => (
              <div 
                key={product.id} 
                className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-orange-200 hover:border-orange-400"
                onClick={() => setSelectedProduct(product)}
              >
                <div className="relative h-48 overflow-hidden bg-orange-50">
                  <InlineEditableImage p={p} field={product.imageField} className="block h-full w-full overflow-hidden">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </InlineEditableImage>
                  {product.badge && (
                    <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: product.badgeColor }}>
                      {product.badge}
                    </div>
                  )}
                  <div className="absolute top-2 right-2 bg-orange-700/80 text-orange-100 text-[10px] font-bold px-2 py-0.5 rounded-full">
                    {product.weight || product.quantity || product.length}
                  </div>
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-1 mb-1">
                    <span className="text-xs text-gray-500">{product.brand}</span>
                    <span className="text-xs text-gray-300">•</span>
                    <span className="text-xs text-gray-500">{product.subCategory}</span>
                  </div>
                  <h3 className="font-semibold text-gray-800 text-sm">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={product.nameKey} 
                      value={product.name}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="building"
                    />
                  </h3>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-lg font-bold text-orange-700">₹<InlineEditableTextWithNav 
                      p={p} 
                      field={product.priceKey} 
                      value={product.price.toString()}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="building"
                    /></span>
                    {product.originalPrice > product.price && (
                      <span className="text-xs text-gray-400 line-through">₹{product.originalPrice}</span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    {product.type}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== PAINTS PAGE ==================== */}
      {activePage === "paints" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-purple-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="paints_badge" 
                value="Paints"
                onNavigate={handleNavigateAfterEdit}
                navPage="paints"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2d1b0e] mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="paints_title" 
                value="Premium Paints"
                onNavigate={handleNavigateAfterEdit}
                navPage="paints"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="paints_subtitle" 
                value="High-quality paints for beautiful finishes"
                onNavigate={handleNavigateAfterEdit}
                navPage="paints"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-purple-600 to-purple-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {editableHardwareProducts.filter(d => d.category === "Paints").map((product) => (
              <div 
                key={product.id} 
                className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-purple-200 hover:border-purple-400"
                onClick={() => setSelectedProduct(product)}
              >
                <div className="relative h-48 overflow-hidden bg-purple-50">
                  <InlineEditableImage p={p} field={product.imageField} className="block h-full w-full overflow-hidden">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </InlineEditableImage>
                  {product.badge && (
                    <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: product.badgeColor }}>
                      {product.badge}
                    </div>
                  )}
                  <div className="absolute top-2 right-2 bg-purple-700/80 text-purple-100 text-[10px] font-bold px-2 py-0.5 rounded-full">
                    {product.volume}
                  </div>
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-1 mb-1">
                    <span className="text-xs text-gray-500">{product.brand}</span>
                    <span className="text-xs text-gray-300">•</span>
                    <span className="text-xs text-gray-500">{product.subCategory}</span>
                  </div>
                  <h3 className="font-semibold text-gray-800 text-sm">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={product.nameKey} 
                      value={product.name}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="paints"
                    />
                  </h3>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-lg font-bold text-purple-700">₹<InlineEditableTextWithNav 
                      p={p} 
                      field={product.priceKey} 
                      value={product.price.toString()}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="paints"
                    /></span>
                    {product.originalPrice > product.price && (
                      <span className="text-xs text-gray-400 line-through">₹{product.originalPrice}</span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    {product.coverage} • {product.type}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== ELECTRICAL PAGE ==================== */}
      {activePage === "electrical" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-green-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="electrical_badge" 
                value="Electrical"
                onNavigate={handleNavigateAfterEdit}
                navPage="electrical"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2d1b0e] mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="electrical_title" 
                value="Electrical Supplies"
                onNavigate={handleNavigateAfterEdit}
                navPage="electrical"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="electrical_subtitle" 
                value="Safety-compliant electrical products"
                onNavigate={handleNavigateAfterEdit}
                navPage="electrical"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-green-600 to-green-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {editableHardwareProducts.filter(d => d.category === "Electrical").map((product) => (
              <div 
                key={product.id} 
                className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-green-200 hover:border-green-400"
                onClick={() => setSelectedProduct(product)}
              >
                <div className="relative h-48 overflow-hidden bg-green-50">
                  <InlineEditableImage p={p} field={product.imageField} className="block h-full w-full overflow-hidden">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </InlineEditableImage>
                  {product.badge && (
                    <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: product.badgeColor }}>
                      {product.badge}
                    </div>
                  )}
                  <div className="absolute top-2 right-2 bg-green-700/80 text-green-100 text-[10px] font-bold px-2 py-0.5 rounded-full">
                    {product.length || product.ways}
                  </div>
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-1 mb-1">
                    <span className="text-xs text-gray-500">{product.brand}</span>
                    <span className="text-xs text-gray-300">•</span>
                    <span className="text-xs text-gray-500">{product.subCategory}</span>
                  </div>
                  <h3 className="font-semibold text-gray-800 text-sm">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={product.nameKey} 
                      value={product.name}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="electrical"
                    />
                  </h3>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-lg font-bold text-green-700">₹<InlineEditableTextWithNav 
                      p={p} 
                      field={product.priceKey} 
                      value={product.price.toString()}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="electrical"
                    /></span>
                    {product.originalPrice > product.price && (
                      <span className="text-xs text-gray-400 line-through">₹{product.originalPrice}</span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    {product.material} • {product.warranty}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== JOURNAL PAGE ==================== */}
      {activePage === "journal" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-orange-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="journal_badge" 
                value="Journal"
                onNavigate={handleNavigateAfterEdit}
                navPage="journal"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2d1b0e] mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="journal_title" 
                value="Hardware Insights"
                onNavigate={handleNavigateAfterEdit}
                navPage="journal"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="journal_subtitle" 
                value="Tips, guides, and insights for your projects"
                onNavigate={handleNavigateAfterEdit}
                navPage="journal"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-orange-600 to-orange-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {editableEditorialPosts.map((post) => (
              <div 
                key={post.id} 
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer border border-orange-200 hover:border-orange-400"
                onClick={(e) => openBlogModal(post, e)}
              >
                <div className="h-56 overflow-hidden">
                  <InlineEditableImage p={p} field={post.imageField} className="block h-full w-full overflow-hidden">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </InlineEditableImage>
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-orange-600 font-semibold">{post.category}</span>
                    <span className="text-xs text-gray-400">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={post.dateKey} 
                        value={post.date}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold text-[#2d1b0e] mb-2">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={post.titleKey} 
                      value={post.title}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="journal"
                    />
                  </h3>
                  <p className="text-gray-600 text-sm line-clamp-2">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={post.descKey} 
                      value={post.desc}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="journal"
                    />
                  </p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-gray-400">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="by_prefix" 
                        value="By"
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />{' '}
                      <span className="font-semibold text-orange-800">
                        <InlineEditableTextWithNav 
                          p={p} 
                          field={post.authorKey} 
                          value={post.author}
                          onNavigate={handleNavigateAfterEdit}
                          navPage="journal"
                        />
                      </span>
                    </span>
                    <button 
                      onClick={(e) => openBlogModal(post, e)}
                      className="text-orange-700 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all"
                    >
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="read_more_button" 
                        value="Read More"
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />
                      <span>→</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== ABOUT PAGE ==================== */}
      {activePage === "about" && (
        <section className="w-full px-4 md:px-8 py-12 md:py-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-orange-700 text-sm tracking-wider uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_badge" 
                  value="About Us"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#2d1b0e] mt-3">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_title" 
                  value="Building Trust Since 1990"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </h2>
              <div className="w-16 h-1 bg-orange-600 mt-4 mb-6" />
              <p className="text-gray-600 leading-relaxed mb-4">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_para1" 
                  value="Hardware Pro has been serving the community with quality tools and building materials for over three decades. We pride ourselves on providing professional-grade products and expert advice to contractors, builders, and DIY enthusiasts."
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_para2" 
                  value="From power tools to plumbing supplies, we stock only the best brands at competitive prices. Our knowledgeable staff is always ready to help you find the right product for your project, ensuring success from start to finish."
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </p>
              <div className="flex flex-wrap gap-6">
                <div>
                  <p className="text-3xl font-bold text-orange-700">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat1_value" 
                      value="1000+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat1_label" 
                      value="Products"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-orange-700">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat2_value" 
                      value="50+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat2_label" 
                      value="Trusted Brands"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-orange-700">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat3_value" 
                      value="20k+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat3_label" 
                      value="Happy Customers"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <InlineEditableImage p={p} field="hardwareAboutImage" className="block h-full w-full overflow-hidden">
                <img 
                  src={p.storeTheme?.hardwareAboutImage || "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/a8415eec-007c-42cb-a0e0-1084d885b373_h1.jpg"} 
                  alt="About Hardware Pro" 
                  className="w-full h-full object-cover" 
                />
              </InlineEditableImage>
            </div>
          </div>
        </section>
      )}
      
      {/* ==================== PRODUCT TABS ==================== */}
      <section className="w-full px-4 md:px-8 mt-8">
        <div className="flex justify-center gap-2 flex-wrap">
          {[
            ["products", "🔧 Shop Hardware"],
            ["services", "🛠 Services"],
            ["details", "📖 About Store"]
          ].map(([key, label]) => (
            <button 
              key={key} 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  p.setActiveTab(key);
                }
              }} 
              className={`px-5 md:px-7 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${p.activeTab === key ? "bg-orange-700 text-white shadow-lg" : "text-gray-500 hover:text-orange-700"}`}
            >
              <InlineEditableTextWithNav 
                p={p} 
                field={`tab_${key}_label`} 
                value={label}
                onNavigate={handleNavigateAfterEdit}
                navPage={activePage}
              />
            </button>
          ))}
        </div>
      </section>
      
      {/* Main Content */}
      <section className="flex-1 w-full px-4 md:px-8 py-8 md:py-12">
        <TabContent p={p} />
      </section>
      
      {/* Newsletter */}
      <section className="bg-gradient-to-r from-[#2d1b0e] to-[#4a2816] py-16 mt-8 w-full">
        <div className="max-w-3xl mx-auto text-center px-4">
          <span className="text-orange-300 text-sm tracking-wider uppercase">
            <InlineEditableTextWithNav 
              p={p} 
              field="newsletter_badge" 
              value="Subscribe"
              onNavigate={handleNavigateAfterEdit}
              navPage="home"
            />
          </span>
          <h3 className="text-2xl md:text-3xl font-bold text-white mt-2">
            <InlineEditableTextWithNav 
              p={p} 
              field="newsletter_title" 
              value="Join the Hardware Community"
              onNavigate={handleNavigateAfterEdit}
              navPage="home"
            />
          </h3>
          <p className="text-orange-200/70 text-sm mt-2">
            <InlineEditableTextWithNav 
              p={p} 
              field="newsletter_subtitle" 
              value="Get DIY tips, exclusive offers, and project ideas"
              onNavigate={handleNavigateAfterEdit}
              navPage="home"
            />
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input 
              type="email" 
              placeholder={p.storefrontText?.email_placeholder || "Your email address"} 
              className="flex-1 px-5 py-3 bg-white/10 border border-orange-400/30 rounded-full text-white placeholder-orange-300/50 focus:border-orange-400 outline-none transition" 
            />
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  // Handle newsletter subscription
                }
              }}
              className="px-6 py-3 rounded-full bg-orange-600 text-white font-semibold hover:bg-orange-500 transition"
            >
              <InlineEditableTextWithNav 
                p={p} 
                field="subscribe_button" 
                value="Subscribe"
                onNavigate={handleNavigateAfterEdit}
                navPage="home"
              />
            </button>
          </div>
        </div>
      </section>
      
      <StoreFooter p={p} />
      
      {/* ==================== MODALS ==================== */}
      
      {/* Product Modal - FIXED with InlineEditableImage */}
      {selectedProduct && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-md w-full bg-white rounded-2xl overflow-hidden shadow-2xl animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="relative h-64">
              <InlineEditableImage p={p} field={selectedProduct.imageField} className="block h-full w-full overflow-hidden">
                <img src={selectedProduct.image} alt={selectedProduct.name} className="w-full h-full object-cover" />
              </InlineEditableImage>
              <button 
                onClick={() => setSelectedProduct(null)} 
                className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/50 text-white hover:bg-orange-600 transition flex items-center justify-center text-xl"
              >
                ✕
              </button>
              {selectedProduct.badge && (
                <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-white text-xs font-bold shadow-lg" style={{ backgroundColor: selectedProduct.badgeColor }}>
                  {selectedProduct.badge}
                </div>
              )}
              <div className="absolute bottom-3 right-3 bg-[#2d1b0e]/80 text-orange-100 text-xs font-bold px-2 py-1 rounded-full">
                {selectedProduct.brand}
              </div>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-gray-500">{selectedProduct.category}</span>
                <span className="text-xs text-gray-300">•</span>
                <span className="text-xs text-gray-500">{selectedProduct.subCategory}</span>
              </div>
              <h3 className="text-2xl font-bold text-[#2d1b0e]">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedProduct.nameKey} 
                  value={selectedProduct.name}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={activePage}
                  isInModal={true}
                />
              </h3>
              <p className="text-gray-600 text-sm mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedProduct.descriptionKey} 
                  value={selectedProduct.description}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={activePage}
                  isInModal={true}
                />
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full">
                  {selectedProduct.subCategory}
                </span>
                {selectedProduct.voltage && (
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                    {selectedProduct.voltage}
                  </span>
                )}
                {selectedProduct.warranty && (
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                    {selectedProduct.warranty}
                  </span>
                )}
              </div>
              <div className="mt-4 flex items-center gap-3">
                <span className="text-3xl font-bold text-orange-700">
                  ₹<InlineEditableTextWithNav 
                    p={p} 
                    field={selectedProduct.priceKey} 
                    value={selectedProduct.price.toString()}
                    onNavigate={handleNavigateAfterEdit}
                    navPage={activePage}
                    isInModal={true}
                  />
                </span>
                {selectedProduct.originalPrice > selectedProduct.price && (
                  <span className="text-sm text-gray-400 line-through">
                    ₹<InlineEditableTextWithNav 
                      p={p} 
                      field={selectedProduct.originalPriceKey} 
                      value={selectedProduct.originalPrice.toString()}
                      onNavigate={handleNavigateAfterEdit}
                      navPage={activePage}
                      isInModal={true}
                    />
                  </span>
                )}
              </div>
              <div className="mt-4 text-sm text-orange-600 text-center bg-orange-50 p-3 rounded-full border border-orange-200">
                🔧 Quality hardware product for your project
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Guide Modal - FIXED with InlineEditableImage */}
      {selectedGuide && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-2xl w-full bg-white rounded-2xl overflow-hidden shadow-2xl animate-slide-up max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 bg-white border-b border-gray-100 p-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-[#2d1b0e]">DIY Guide</h3>
              <button 
                onClick={closeGuideModal} 
                className="w-10 h-10 rounded-full bg-gray-100 text-gray-600 hover:bg-orange-600 hover:text-white transition flex items-center justify-center text-xl"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <InlineEditableImage p={p} field={selectedGuide.imageField} className="block w-full overflow-hidden rounded-xl">
                  <img src={selectedGuide.image} alt={selectedGuide.title} className="w-full h-64 object-cover" />
                </InlineEditableImage>
              </div>
              <div className="mb-3">
                <span className="inline-block px-3 py-1 bg-orange-100 text-orange-800 text-xs rounded-full font-semibold">
                  {selectedGuide.difficulty}
                </span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#2d1b0e] mb-4">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedGuide.titleKey} 
                  value={selectedGuide.title}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="guides"
                  isInModal={true}
                />
              </h2>
              <p className="text-gray-600 mb-6">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedGuide.descKey} 
                  value={selectedGuide.desc}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="guides"
                  isInModal={true}
                />
              </p>
              <div className="space-y-4">
                <p className="font-semibold text-[#2d1b0e]">Steps:</p>
                {selectedGuide.steps?.map((step, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-orange-50 rounded-xl">
                    <span className="w-8 h-8 rounded-full bg-orange-700 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                      {idx + 1}
                    </span>
                    <p className="text-gray-700 text-sm">{step}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex justify-end">
                <button 
                  onClick={closeGuideModal} 
                  className="px-6 py-2 rounded-full bg-orange-700 text-white hover:bg-orange-800 transition"
                >
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="close_button" 
                    value="Close"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="guides"
                    isInModal={true}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Blog Modal - FIXED with InlineEditableImage */}
      {selectedPost && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-2xl w-full bg-white rounded-2xl overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 bg-white border-b border-gray-100 p-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-[#2d1b0e]">Hardware Insights</h3>
              <button 
                onClick={closeBlogModal} 
                className="w-10 h-10 rounded-full bg-gray-100 text-gray-600 hover:bg-orange-600 hover:text-white transition flex items-center justify-center text-xl"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <InlineEditableImage p={p} field={selectedPost.imageField} className="block w-full overflow-hidden rounded-xl">
                  <img src={selectedPost.image} alt={selectedPost.title} className="w-full h-64 object-cover" />
                </InlineEditableImage>
              </div>
              <div className="mb-3">
                <span className="inline-block px-3 py-1 bg-orange-100 text-orange-800 text-xs rounded-full font-semibold">
                  {selectedPost.category}
                </span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#2d1b0e] mb-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedPost.titleKey} 
                  value={selectedPost.title}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="journal"
                  isInModal={true}
                />
              </h2>
              <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 border-b border-gray-100 pb-4 mb-4">
                <span>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedPost.dateKey} 
                    value={selectedPost.date}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </span>
                <span>•</span>
                <span>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="by_prefix" 
                    value="By"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />{' '}
                  <span className="font-semibold text-orange-800">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={selectedPost.authorKey} 
                      value={selectedPost.author}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="journal"
                      isInModal={true}
                    />
                  </span>
                </span>
              </div>
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedPost.fullContentKey} 
                    value={selectedPost.fullContent}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </p>
              </div>
              <div className="mt-8 pt-4 border-t border-gray-100 flex justify-end">
                <button 
                  onClick={closeBlogModal} 
                  className="px-6 py-2 rounded-full bg-orange-700 text-white hover:bg-orange-800 transition"
                >
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="close_button" 
                    value="Close"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <style jsx>{`
        @keyframes marquee { 0% { transform: translateX(100%); } 100% { transform: translateX(-100%); } }
        .animate-marquee { animation: marquee 20s linear infinite; }
        
        @keyframes float-particle { 0%,100% { transform: translateY(0px) translateX(0px); opacity: 0.05; } 50% { transform: translateY(-40px) translateX(20px); opacity: 0.2; } }
        .animate-float-particle { animation: float-particle 6s ease-in-out infinite; }
        
        @keyframes word-pop { 0% { opacity: 0; transform: scale(0.5) rotate(-8deg); } 60% { transform: scale(1.08) rotate(2deg); } 100% { opacity: 1; transform: scale(1) rotate(0); } }
        .animate-word-pop { animation: word-pop 0.6s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes slide-right { from { opacity: 0; transform: translateX(-40px); } to { opacity: 1; transform: translateX(0); } }
        .animate-slide-right { animation: slide-right 0.7s ease-out forwards; opacity: 0; animation-fill-mode: forwards; }
        
        @keyframes bounce-in { 0% { opacity: 0; transform: scale(0.3); } 50% { transform: scale(1.05); } 70% { transform: scale(0.95); } 100% { opacity: 1; transform: scale(1); } }
        .animate-bounce-in { animation: bounce-in 0.7s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes card-fly { 0% { opacity: 0; transform: translateY(40px) scale(0.9); } 60% { transform: translateY(-5px) scale(1.02); } 100% { opacity: 1; transform: translateY(0) scale(1); } }
        .animate-card-fly { animation: card-fly 0.6s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes scroll-dot { 0% { transform: translateY(0); opacity: 1; } 100% { transform: translateY(12px); opacity: 0; } }
        .animate-scroll-dot { animation: scroll-dot 1.5s ease-in-out infinite; }
        
        @keyframes slide-up { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .animate-slide-up { animation: slide-up 0.5s ease-out forwards; opacity: 0; animation-fill-mode: forwards; }
        
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        .animate-fade-in { animation: fade-in 0.3s ease-out forwards; }
        
        @keyframes bounce { 0%,100% { transform: translateX(-50%) translateY(0); } 50% { transform: translateX(-50%) translateY(-8px); } }
        .animate-bounce-slow { animation: bounce 2s ease-in-out infinite; }
        
        @keyframes slide-up-modal { from { opacity: 0; transform: translateY(30px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
        .animate-slide-up { animation: slide-up-modal 0.4s ease-out forwards; }
        
        .line-clamp-1 { display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; }
        .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
      `}</style>
    </div>
  );
}